/**
 * Heartbeat manages a periodic ping with a per-cycle pong-wait timer.
 *
 * Why this exists: API Gateway WebSocket closes idle connections at 10 minutes
 * and silently half-closes on some network transitions. A one-shot pong timer
 * detects only the very first failure; we want to detect *every* missed pong.
 * notePong() must be called whenever a pong is received so the current cycle's
 * timer is cleared and the next ping arms a fresh one.
 */

export interface HeartbeatOptions {
   intervalMs: number
   pongTimeoutMs: number
   send: () => void
   onTimeout: () => void
}

export default class Heartbeat {
   private intervalHandle: ReturnType<typeof setInterval> | null = null
   private pongTimerHandle: ReturnType<typeof setTimeout> | null = null
   private readonly opts: HeartbeatOptions

   constructor(opts: HeartbeatOptions) {
      this.opts = opts
   }

   start = () => {
      this.stop()
      this.intervalHandle = setInterval(() => this.tick(), this.opts.intervalMs)
   }

   stop = () => {
      if (this.intervalHandle) {
         clearInterval(this.intervalHandle)
         this.intervalHandle = null
      }
      if (this.pongTimerHandle) {
         clearTimeout(this.pongTimerHandle)
         this.pongTimerHandle = null
      }
   }

   notePong = () => {
      if (this.pongTimerHandle) {
         clearTimeout(this.pongTimerHandle)
         this.pongTimerHandle = null
      }
   }

   private tick() {
      this.opts.send()
      if (this.pongTimerHandle) clearTimeout(this.pongTimerHandle)
      this.pongTimerHandle = setTimeout(() => {
         this.pongTimerHandle = null
         this.opts.onTimeout()
      }, this.opts.pongTimeoutMs)
   }
}
